import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { checkStripeMinimum, getMinimumAmountError } from '../../utils/stripeMinimums';
import { downloadInvoice } from '../../utils/invoiceDownload';
import InvoicePreview from '../shared/InvoicePreview';
import EarlyPaymentBanner from '../shared/EarlyPaymentBanner';
import { getDisplayAmount } from '../../utils/amountUtils';
import {
  Search,
  Filter,
  Eye,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  FileText,
  FileSpreadsheet,
  Calendar,
  PoundSterling,
  User,
  Clock,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Plus,
  MoreHorizontal,
  Paperclip,
  CreditCard,
  RefreshCw
} from 'lucide-react';

const PAID_STATUS_VALUES = ['paid', 'completed', 'settled', 'fulfilled'];

const isInvoicePaid = (invoice = {}) => {
  const status = String(invoice.status || '').toLowerCase();
  const approvalStatus = String(invoice.approvalStatus || '').toLowerCase();
  const paymentStatus = String(invoice.paymentStatus || '').toLowerCase();

  if (PAID_STATUS_VALUES.includes(status)) return true;
  if (PAID_STATUS_VALUES.includes(paymentStatus)) return true;
  if (approvalStatus === 'approved_and_paid') return true;
  if (invoice.paymentDate || invoice.paidAt || invoice.isPaid) return true;
  if (Number(invoice.amountPaid || 0) > 0 && Number(invoice.amountPaid || 0) >= Number(invoice.amount || 0)) return true;

  return false;
};

const ReceivedInvoices = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [amountRange, setAmountRange] = useState({ min: '', max: '' });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [actionModal, setActionModal] = useState({ open: false, type: '', invoice: null });
  const [rejectReason, setRejectReason] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const invoicesPerPage = 10; // Number of invoices to display per page
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [highlightedInvoiceId, setHighlightedInvoiceId] = useState(null);
  const [approvingInvoices, setApprovingInvoices] = useState(new Set()); // Track invoices being approved
  const [approvalErrors, setApprovalErrors] = useState(new Map()); // Track invoices with confirmed approval failures

  // Function to handle export of selected invoices as CSV
  const handleExportSelected = () => {
    try {
      if (selectedInvoices.length === 0) {
        alert('Please select invoices to export');
        return;
      }

      // Get the selected invoice data
      const selectedInvoiceData = invoices.filter(invoice => 
        selectedInvoices.includes(invoice.id)
      );

      if (selectedInvoiceData.length === 0) {
        alert('No invoice data found for export');
        return;
      }

      // Create CSV content with proper escaping
      const csvHeaders = [
        'Invoice Number',
        'Supplier Name',
        'Supplier Email',
        'Amount',
        'Status',
        'Due Date',
        'Received Date',
        'Description',
        'Approval Status'
      ];

      const csvRows = selectedInvoiceData.map(invoice => [
        invoice.invoiceNumber || '',
        invoice.supplier?.name || '',
        invoice.supplier?.email || '',
        `£${(parseFloat(invoice.amount) || 0).toFixed(2)}`,
        (invoice.status || '').replace(/-/g, ' ').toUpperCase(),
        invoice.dueDate ? formatDate(invoice.dueDate) : '',
        invoice.receivedDate ? formatDate(invoice.receivedDate) : '',
        (invoice.description || '').replace(/"/g, '""'), // Escape quotes
        (invoice.approvalStatus || '').replace(/-/g, ' ').toUpperCase()
      ]);

      // Convert to CSV format
      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => 
          row.map(cell => `"${cell}"`).join(',')
        )
      ].join('\n');

      // Create and download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `received-invoices-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Clear selection
      setSelectedInvoices([]);
      alert(`Successfully exported ${selectedInvoiceData.length} invoice(s) to CSV`);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export invoices. Please try again.');
    }
  };

  // Function to handle export of selected invoices as PDF
  const handleExportPDF = () => {
    try {
      if (selectedInvoices.length === 0) {
        alert('Please select invoices to export');
        return;
      }

      const selectedInvoiceData = invoices.filter(invoice => 
        selectedInvoices.includes(invoice.id)
      );

      if (selectedInvoiceData.length === 0) {
        alert('No invoice data found for export');
        return;
      }

      // Create a new window for PDF generation
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Received Invoices Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; text-align: center; margin-bottom: 30px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .amount { text-align: right; }
            .status { text-transform: capitalize; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>Received Invoices Report</h1>
          <p>Generated on: ${new Date().toLocaleDateString()}</p>
          <p>Total Invoices: ${selectedInvoiceData.length}</p>
          <table>
            <thead>
              <tr>
                <th>Invoice Number</th>
                <th>Supplier Name</th>
                <th>Supplier Email</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Due Date</th>
                <th>Received Date</th>
                <th>Description</th>
                <th>Approval Status</th>
              </tr>
            </thead>
            <tbody>
              ${selectedInvoiceData.map(invoice => `
                <tr>
                  <td>${invoice.invoiceNumber || ''}</td>
                  <td>${invoice.supplier?.name || ''}</td>
                  <td>${invoice.supplier?.email || ''}</td>
                  <td class="amount">£${(parseFloat(invoice.amount) || 0).toFixed(2)}</td>
                  <td class="status">${(invoice.status || '').replace(/-/g, ' ').toUpperCase()}</td>
                  <td>${invoice.dueDate ? formatDate(invoice.dueDate) : ''}</td>
                  <td>${invoice.receivedDate ? formatDate(invoice.receivedDate) : ''}</td>
                  <td>${invoice.description || ''}</td>
                  <td class="status">${(invoice.approvalStatus || '').replace(/-/g, ' ').toUpperCase()}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="no-print">
            <p>This report can be printed or saved as PDF using your browser's print function.</p>
            <button onclick="window.print()">Print / Save as PDF</button>
            <button onclick="window.close()">Close</button>
          </div>
        </body>
        </html>
      `);
      printWindow.document.close();
      
      // Clear selection
      setSelectedInvoices([]);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Failed to export invoices as PDF. Please try again.');
    }
  };

  // Function to toggle invoice selection
  const toggleInvoiceSelection = (invoiceId) => {
    setSelectedInvoices(prev => {
      if (prev.includes(invoiceId)) {
        return prev.filter(id => id !== invoiceId);
      } else {
        return [...prev, invoiceId];
      }
    });
  };

  // NEW: Simplified download handlers for InvoicePreview modal
  const handleDownloadPDF = (invoice, options = {}) => {
    console.log('📄 ReceivedInvoices - handleDownloadPDF called with invoice:', invoice);
    handleDownload('pdf', invoice, options);
  };

  const handleDownloadExcel = (invoice, options = {}) => {
    handleDownload('excel', invoice, options);
  };

  const triggerDownloadLink = (downloadUrl, filename) => {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const attemptDirectDownload = async (format, invoice) => {
    const endpoint =
      format === 'pdf'
        ? `/invoices/${invoice.id}/download-pdf`
        : `/invoices/${invoice.id}/download-excel`;
    const filename = `invoice-${invoice.invoiceNumber || invoice.id}.${
      format === 'pdf' ? 'pdf' : 'csv'
    }`;

    const response = await api.get(endpoint, {
      responseType: 'blob'
    });

    if (response.status !== 200) {
      return false;
    }

    const contentType = response.headers?.['content-type'] || '';

    if (contentType.includes('application/json')) {
      const payloadText =
        response.data instanceof Blob && response.data.text
          ? await response.data.text()
          : response.data;
      let payload;
      try {
        payload =
          typeof payloadText === 'string' ? JSON.parse(payloadText) : payloadText;
      } catch (jsonError) {
        console.error('Failed to parse download response JSON', jsonError);
        throw jsonError;
      }

      const downloadUrl = payload?.data?.downloadUrl || payload?.downloadUrl;
      if (downloadUrl) {
        triggerDownloadLink(downloadUrl, filename);
        return true;
      }

      const message = payload?.error || payload?.message;
      if (message) {
        throw new Error(message);
      }
      return false;
    }

    const blob =
      response.data instanceof Blob
        ? response.data
        : new Blob([response.data], {
            type: format === 'pdf' ? 'application/pdf' : 'text/csv'
          });

    const url = window.URL.createObjectURL(blob);
    triggerDownloadLink(url, filename);
    window.URL.revokeObjectURL(url);
    return true;
  };

  // Legacy renderer used as fallback if API download fails
  const legacyHandleDownload = async (format, invoice) => {
    try {
      setLoading(true);

      let handledDirectly = false;
      if (format === 'pdf' || format === 'csv' || format === 'excel') {
        try {
          handledDirectly = await attemptDirectDownload(format, invoice);
        } catch (directError) {
          console.error('Invoice download API failed, falling back to legacy renderer', directError);
        }
      }
      if (handledDirectly) {
        return;
      }

      if (format === 'pdf') {
        // Generate PDF using browser print functionality
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Invoice ${invoice.invoiceNumber || 'Invoice'}</title>
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
              
              body { 
                font-family: 'Inter', 'Segoe UI', system-ui, sans-serif; 
                margin: 40px; 
                color: #1f2937;
                background: #ffffff;
                line-height: 1.6;
              }
              
              .invoice-container {
                max-width: 800px;
                margin: 0 auto;
                background: white;
                border-radius: 16px;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                overflow: hidden;
              }
              
              .invoice-header {
                text-align: center;
                padding: 40px 30px;
                border-bottom: 1px solid #e2e8f0;
                background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
              }
              
              .invoice-title {
                font-size: 36px;
                font-weight: 300;
                color: #1f2937;
                margin: 0 0 12px 0;
                letter-spacing: 2px;
              }
              
              .title-line {
                width: 80px;
                height: 2px;
                background: #94a3b8;
                margin: 0 auto 24px auto;
              }
              
              .invoice-meta {
                display: flex;
                justify-content: center;
                gap: 40px;
                font-size: 14px;
                color: #64748b;
              }
              
              .meta-item {
                text-align: center;
              }
              
              .meta-label {
                font-size: 11px;
                font-weight: 600;
                color: #94a3b8;
                text-transform: uppercase;
                letter-spacing: 1px;
                margin-bottom: 4px;
              }
              
              .meta-value {
                font-weight: 500;
                color: #475569;
              }
              
              .company-section {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 32px;
                padding: 40px;
              }
              
              .company-card {
                background: #f8fafc;
                border: 1px solid #e2e8f0;
                border-radius: 16px;
                padding: 32px;
              }
              
              .company-header {
                display: flex;
                align-items: center;
                margin-bottom: 24px;
              }
              
              .company-dot {
                width: 8px;
                height: 8px;
                background: #64748b;
                border-radius: 50%;
                margin-right: 12px;
              }
              
              .company-card h3 {
                font-size: 12px;
                font-weight: 600;
                color: #64748b;
                text-transform: uppercase;
                letter-spacing: 1px;
                margin: 0;
              }
              
              .company-card p {
                margin: 6px 0;
                font-size: 14px;
                color: #475569;
                line-height: 1.5;
              }
              
              .company-card p:first-of-type {
                font-weight: 600;
                color: #1e293b;
                font-size: 16px;
                margin-top: 0;
              }
              
              .line-items-section {
                padding: 0 40px 40px 40px;
              }
              
              .line-items-header {
                display: flex;
                align-items: center;
                margin-bottom: 24px;
              }
              
              .line-items-section h3 {
                font-size: 12px;
                font-weight: 600;
                color: #64748b;
                text-transform: uppercase;
                letter-spacing: 1px;
                margin: 0;
              }
              
              .line-items-table {
                background: white;
                border: 1px solid #e2e8f0;
                border-radius: 16px;
                overflow: hidden;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
              }
              
              .line-items-table table {
                width: 100%;
                border-collapse: collapse;
              }
              
              .line-items-table th {
                background: #f8fafc;
                border-bottom: 1px solid #e2e8f0;
                padding: 16px 24px;
                text-align: left;
                font-size: 11px;
                font-weight: 600;
                color: #64748b;
                text-transform: uppercase;
                letter-spacing: 1px;
              }
              
              .line-items-table td {
                padding: 16px 24px;
                border-bottom: 1px solid #f1f5f9;
                font-size: 14px;
                color: #475569;
              }
              
              .line-items-table tr:last-child td {
                border-bottom: none;
              }
              
              .line-items-table .amount {
                text-align: right;
                font-weight: 500;
              }
              
              .empty-state {
                text-align: center;
                padding: 48px 24px;
                color: #94a3b8;
              }
              
              .empty-state p {
                font-size: 14px;
                font-weight: 500;
                margin-bottom: 4px;
              }
              
              .empty-state .subtitle {
                font-size: 12px;
                color: #cbd5e1;
              }
              
              .totals-section {
                padding: 0 40px 40px 40px;
                display: flex;
                justify-content: flex-end;
              }
              
              .totals-card {
                width: 384px;
                background: #f8fafc;
                border: 1px solid #e2e8f0;
                border-radius: 16px;
                padding: 32px;
              }
              
              .total-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 8px 0;
                font-size: 14px;
              }
              
              .total-row.grand-total {
                border-top: 1px solid #cbd5e1;
                padding-top: 24px;
                margin-top: 16px;
                font-size: 18px;
                font-weight: 600;
                color: #1e293b;
              }
              
              .grand-total .total-amount {
                font-size: 24px;
                font-weight: 700;
              }
              
              .no-print {
                text-align: center;
                padding: 24px;
                background: #f8fafc;
                border-top: 1px solid #e2e8f0;
              }
              
              .no-print button {
                background: #475569;
                color: white;
                border: none;
                padding: 12px 24px;
                border-radius: 8px;
                cursor: pointer;
                margin: 0 8px;
                font-size: 14px;
                font-weight: 500;
              }
              
              .no-print button:hover {
                background: #334155;
              }
              
              @page {
                margin: 0.5in !important;
                size: A4 !important;
              }
              
              @media print {
                * {
                  -webkit-print-color-adjust: exact !important;
                  color-adjust: exact !important;
                }
                body { 
                  margin: 0 !important; 
                  padding: 0 !important;
                  font-size: 12px;
                  background: white !important;
                }
                html {
                  background: white !important;
                }
                .no-print { display: none !important; }
                .invoice-container { 
                  max-width: none !important;
                  box-shadow: none !important;
                  border-radius: 0 !important;
                  margin: 0 !important;
                  padding: 0 !important;
                }
                .invoice-header { 
                  background: white !important;
                  border-bottom: 1px solid #e2e8f0;
                }
                .company-section { 
                  page-break-inside: avoid;
                }
                .line-items-table { 
                  page-break-inside: avoid;
                }
                .totals-section { 
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <div class="invoice-container">
              <!-- Invoice Header -->
              <div class="invoice-header">
                <h1 class="invoice-title">INVOICE</h1>
                <div class="title-line"></div>
              <div class="invoice-meta">
                <div class="meta-item">
                  <div class="meta-label">Invoice Date</div>
                  <div class="meta-value">${new Date(invoice.createdAt || invoice.sentDate).toLocaleDateString()}</div>
                </div>
                <div class="meta-item">
                  <div class="meta-label">Invoice Number</div>
                  <div class="meta-value">${invoice.invoiceNumber}</div>
                </div>
                <div class="meta-item">
                  <div class="meta-label">Due Date</div>
                  <div class="meta-value">${new Date(invoice.dueDate).toLocaleDateString()}</div>
                </div>
              </div>
              ${invoice.status || invoice.approvalStatus ? `
              <div style="margin-top: 16px; text-align: center;">
                <span style="display: inline-block; padding: 6px 16px; background: #fef3c7; color: #92400e; border-radius: 8px; font-size: 11px; font-weight: 600; text-transform: uppercase;">
                  ${(invoice.approvalStatus || invoice.status || '').replace(/_/g, ' ')}
                </span>
              </div>
              ` : ''}
              ${invoice.poNumber ? `
              <div style="margin-top: 12px; text-align: center; font-size: 12px; color: #64748b;">
                <strong>PO Number:</strong> ${invoice.poNumber}
              </div>
              ` : ''}
              </div>
              
              <!-- Company Information -->
              <div class="company-section">
                <div class="company-card">
                  <div class="company-header">
                    <div class="company-dot"></div>
                    <h3>From</h3>
                  </div>
                  <p><strong>${invoice.sender?.companyName || (invoice.sender?.firstName + ' ' + invoice.sender?.lastName) || '[Company Name]'}</strong></p>
                  ${invoice.sender?.firstName || invoice.sender?.lastName ? `<p>${invoice.sender?.firstName || ''} ${invoice.sender?.lastName || ''}</p>` : ''}
                  <p>${invoice.sender?.email || '[Email]'}</p>
                  ${invoice.sender?.address ? `<p>${invoice.sender.address}</p>` : ''}
                  ${invoice.sender?.city || invoice.sender?.state || invoice.sender?.zipCode ? 
                    `<p>${[invoice.sender?.city, invoice.sender?.state, invoice.sender?.zipCode].filter(Boolean).join(', ')}</p>` 
                    : ''}
                  ${invoice.sender?.country ? `<p>${invoice.sender.country}</p>` : ''}
                </div>
                
                <div class="company-card">
                  <div class="company-header">
                    <div class="company-dot"></div>
                    <h3>To</h3>
                  </div>
                  <p><strong>${invoice.receiver?.companyName || (invoice.receiver?.firstName + ' ' + invoice.receiver?.lastName) || '[Company Name]'}</strong></p>
                  ${invoice.receiver?.firstName || invoice.receiver?.lastName ? `<p>${invoice.receiver?.firstName || ''} ${invoice.receiver?.lastName || ''}</p>` : ''}
                  <p>${invoice.receiver?.email || '[Email]'}</p>
                  ${invoice.receiver?.address ? `<p>${invoice.receiver.address}</p>` : ''}
                  ${invoice.receiver?.city || invoice.receiver?.state || invoice.receiver?.zipCode ? 
                    `<p>${[invoice.receiver?.city, invoice.receiver?.state, invoice.receiver?.zipCode].filter(Boolean).join(', ')}</p>` 
                    : ''}
                  ${invoice.receiver?.country ? `<p>${invoice.receiver.country}</p>` : ''}
                </div>
              </div>
              
              <!-- Line Items -->
              <div class="line-items-section">
                <div class="line-items-header">
                  <div class="company-dot"></div>
                  <h3>Line Items</h3>
                </div>
                <div class="line-items-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th style="text-align: center;">Qty</th>
                        <th style="text-align: right;">Unit Price</th>
                        <th style="text-align: right;">Tax %</th>
                        <th style="text-align: right;">Discount %</th>
                        <th style="text-align: right;">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${invoice.lineItems && invoice.lineItems.length > 0 ? 
                        invoice.lineItems.map(item => {
                          const subtotal = (item.quantity || 0) * (item.unitPrice || 0);
                          const discount = subtotal * ((item.discount || 0) / 100);
                          const afterDiscount = subtotal - discount;
                          const tax = afterDiscount * ((item.taxRate || 0) / 100);
                          const total = afterDiscount + tax;
                          
                          const formattedUnitPrice = item.unitPrice < 0 ? `(£${Math.abs(item.unitPrice).toFixed(2)})` : `£${(item.unitPrice || 0).toFixed(2)}`;
                          const formattedTotal = total < 0 ? `(£${Math.abs(total).toFixed(2)})` : `£${total.toFixed(2)}`;
                          return `
                            <tr>
                              <td>${item.description || '[Item description]'}</td>
                              <td style="text-align: center;">${item.quantity || 0}</td>
                              <td class="amount">${formattedUnitPrice}</td>
                              <td style="text-align: right;">${item.taxRate || 0}%</td>
                              <td style="text-align: right;">${item.discount || 0}%</td>
                              <td class="amount">${formattedTotal}</td>
                            </tr>
                          `;
                        }).join('') : 
                        '<tr><td colspan="6"><div class="empty-state"><p>No line items found</p><div class="subtitle">Add items to this invoice</div></div></td></tr>'
                      }
                    </tbody>
                  </table>
                </div>
              </div>
              
              <!-- Totals -->
              <div class="totals-section">
                <div class="totals-card">
                  <div class="total-row">
                    <span style="color: #64748b;">Subtotal:</span>
                    <span style="font-weight: 500;">£${(parseFloat(invoice.subtotal) || 0).toFixed(2)}</span>
                  </div>
                  <div class="total-row">
                    <span style="color: #64748b;">Tax:</span>
                    <span style="font-weight: 500;">£${(parseFloat(invoice.tax) || 0).toFixed(2)}</span>
                  </div>
                  ${invoice.discountAmount && parseFloat(invoice.discountAmount) > 0 ? `
                  <div class="total-row" style="background: #f9f9f9; margin: 8px -16px; padding: 8px 16px; border-top: 1px dashed #999; border-bottom: 1px dashed #999;">
                    <span style="color: #555; font-style: italic;">
                      ✨ Early Payment Discount
                      ${invoice.discountSourceInvoice?.invoiceNumber ? `<br><span style="font-size: 10px; font-style: normal;">(from ${invoice.discountSourceInvoice.invoiceNumber})</span>` : ''}:
                    </span>
                    <span style="color: #555; font-style: italic; font-weight: 500;">-£${(parseFloat(invoice.discountAmount)).toFixed(2)}</span>
                  </div>
                  ` : ''}
                  <div class="total-row grand-total">
                    <span>Total:</span>
                    <span class="total-amount">£${(parseFloat(invoice.amount) || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              ${invoice.notes ? `
              <div style="padding: 0 40px 20px 40px;">
                <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px;">
                  <h4 style="font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; margin: 0 0 12px 0;">Notes:</h4>
                  <p style="margin: 0; font-size: 13px; color: #475569; line-height: 1.6;">${invoice.notes}</p>
                </div>
              </div>
              ` : ''}
              
              ${invoice.terms ? `
              <div style="padding: 0 40px 20px 40px;">
                <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px;">
                  <h4 style="font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; margin: 0 0 12px 0;">Terms & Conditions:</h4>
                  <p style="margin: 0; font-size: 13px; color: #475569; line-height: 1.6;">${invoice.terms}</p>
                </div>
              </div>
              ` : ''}
              
              <div class="no-print">
                <p>This invoice can be printed or saved as PDF using your browser's print function.</p>
                <button onclick="window.print()">Print / Save as PDF</button>
                <button onclick="window.close()">Close</button>
              </div>
            </div>
          </body>
          </html>
        `);
        printWindow.document.close();
      } else if (format === 'csv' || format === 'excel') {
        // Generate CSV
        const csvContent = [
          ['Invoice Number', 'Supplier', 'Amount', 'Status', 'Due Date', 'Received Date'],
          [
            invoice.invoiceNumber || '',
            invoice.sender?.companyName || invoice.sender?.firstName + ' ' + invoice.sender?.lastName || '',
            `£${(invoice.amount || 0).toFixed(2)}`,
            (invoice.status || '').replace(/-/g, ' ').toUpperCase(),
            invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '',
            invoice.receivedDate ? new Date(invoice.receivedDate).toLocaleDateString() : ''
          ]
        ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `received-invoice-${invoice.invoiceNumber || 'unknown'}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      
      alert(`Successfully downloaded invoice as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Download error:', error);
      alert(`Failed to download invoice as ${format.toUpperCase()}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch received invoices from API
  const fetchReceivedInvoices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/invoices?role=receiver');
      
      if (response.data.data && response.data.data.invoices) {
        // Transform backend data to match frontend structure
        const transformedInvoices = response.data.data.invoices.map(invoice => ({
          id: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
          // For InvoicePreview component - sender info (who sent the invoice)
          sender: invoice.sender ? {
            companyName: invoice.sender.companyName,
            firstName: invoice.sender.firstName,
            lastName: invoice.sender.lastName,
            email: invoice.sender.email,
            address: invoice.sender.address,
            city: invoice.sender.city,
            state: invoice.sender.state,
            zipCode: invoice.sender.zipCode,
            country: invoice.sender.country,
            subscriptionPlan: invoice.sender.subscriptionPlan
          } : null,
          // For InvoicePreview component - receiver info (prefer backend receiver with full address; fallback to current user)
          receiver: invoice.receiver ? {
            companyName: invoice.receiver.companyName,
            firstName: invoice.receiver.firstName,
            lastName: invoice.receiver.lastName,
            email: invoice.receiver.email,
            address: invoice.receiver.address,
            city: invoice.receiver.city,
            state: invoice.receiver.state,
            zipCode: invoice.receiver.zipCode,
            country: invoice.receiver.country,
            subscriptionPlan: invoice.receiver.subscriptionPlan
          } : {
            companyName: user?.companyName,
            firstName: user?.firstName,
            lastName: user?.lastName,
            email: user?.email,
            address: user?.address,
            city: user?.city,
            state: user?.state,
            zipCode: user?.zipCode,
            country: user?.country,
            subscriptionPlan: user?.subscriptionPlan
          },
          // For table display - supplier info
          supplier: {
            name: invoice.sender?.companyName || `${invoice.sender?.firstName} ${invoice.sender?.lastName}`,
            email: invoice.sender?.email,
            address: invoice.sender?.address
          },
          amount: getDisplayAmount(invoice.total),
          status: invoice.status,
          paymentDate: invoice.paymentDate || invoice.payment_date,
          dueDate: invoice.dueDate,
          receivedDate: invoice.createdAt || invoice.created_at,
          createdAt: invoice.createdAt || invoice.created_at,
          description: invoice.notes || '',
          lineItems: invoice.items || [],
          notes: invoice.notes,
          terms: invoice.terms,
          poNumber: invoice.poNumber,
          attachments: invoice.metadata?.attachments || [],
          approvalStatus: invoice.approvalStatus,
          rejectionReason: invoice.rejectionReason,
          discounts: invoice.discounts || invoice.earlyPaymentDiscounts || [], // Map backend discounts field
          // Financial fields for InvoicePreview
          subtotal: invoice.subtotal,
          tax: invoice.tax,
          discountAmount: invoice.discountAmount,
          appliedDiscountId: invoice.appliedDiscountId,
          discountSourceInvoiceId: invoice.discountSourceInvoiceId,
          discountSourceInvoice: invoice.discountSourceInvoice,
          // Map refund fields
          refundStatus: invoice.refundStatus,
          refundAmount: invoice.refundAmount,
          refundDate: invoice.refundDate,
          refundReason: invoice.refundReason,
          stripeRefundId: invoice.stripeRefundId
        }));
        
        setInvoices(transformedInvoices);
        setFilteredInvoices(transformedInvoices);
        
        // If no invoices returned from API, show empty state
        if (transformedInvoices.length === 0) {
          // No received invoices from API, showing empty state
          setInvoices([]);
          setFilteredInvoices([]);
        }
      } else {
        console.error('Failed to fetch received invoices:', response.data.message);
        setInvoices([]);
        setFilteredInvoices([]);
        // Show empty state when API fails
        setInvoices([]);
        setFilteredInvoices([]);
      }
    } catch (error) {
      console.error('Error fetching received invoices:', error);
      setInvoices([]);
      setFilteredInvoices([]);
      // Show empty state when API fails
      setInvoices([]);
      setFilteredInvoices([]);
    } finally {
      setLoading(false);
    }
  };



  // Fetch received invoices on component mount
  useEffect(() => {
    if (user) {
      fetchReceivedInvoices();
    }
  }, [user]);

  // Check for highlight parameter to highlight specific invoice
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId) {
      setHighlightedInvoiceId(highlightId);
      // Auto-remove highlight after 5 seconds
      const timer = setTimeout(() => {
        setHighlightedInvoiceId(null);
        // Remove highlight parameter from URL
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('highlight');
        setSearchParams(newParams, { replace: true });
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.actions-dropdown')) {
        setOpenDropdowns({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Listen for notification clicks to auto-open invoice preview
  useEffect(() => {
    const handleOpenInvoicePreview = (event) => {
      const { invoiceId } = event.detail;
      if (invoiceId && invoices.length > 0) {
        const invoice = invoices.find(inv => inv.id === invoiceId);
        if (invoice) {
          // Fetch full invoice details with line items
          (async () => {
            try {
              setLoading(true);
              const response = await api.get(`/invoices/${invoice.id}`);
              console.log('🔍 Notification click - Full invoice API response:', response.data);
              
              const fullInvoice = response.data.data || response.data;
              console.log('🔍 Notification click - Extracted invoice:', fullInvoice);
              console.log('🔍 Notification click - Line items:', fullInvoice.items || fullInvoice.lineItems);
              
              // Merge full data with existing invoice data
              const completeInvoice = {
                ...invoice,
                ...fullInvoice,
                lineItems: fullInvoice.items || fullInvoice.lineItems || invoice.lineItems || [],
                amount: fullInvoice.total || invoice.amount
              };
              
              console.log('🔍 Notification click - Complete invoice:', {
                invoiceNumber: completeInvoice.invoiceNumber,
                lineItems: completeInvoice.lineItems,
                lineItemsCount: completeInvoice.lineItems?.length,
                amount: completeInvoice.amount,
                total: completeInvoice.total
              });
              
              setPreviewInvoice(completeInvoice);
              setShowPreview(true);
              setHighlightedInvoiceId(invoiceId);
              
              // Remove highlight after 5 seconds
              setTimeout(() => setHighlightedInvoiceId(null), 5000);
            } catch (error) {
              console.error('Error fetching full invoice details:', error);
              // Fallback to basic invoice data
              setPreviewInvoice(invoice);
              setShowPreview(true);
              setHighlightedInvoiceId(invoiceId);
              setTimeout(() => setHighlightedInvoiceId(null), 5000);
            } finally {
              setLoading(false);
            }
          })();
        }
      }
    };

    window.addEventListener('openInvoicePreview', handleOpenInvoicePreview);
    return () => window.removeEventListener('openInvoicePreview', handleOpenInvoicePreview);
  }, [invoices, setLoading]);

  // Search and filter functionality
  useEffect(() => {
    let filtered = invoices;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(invoice =>
        invoice.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        invoice.supplier.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        invoice.supplier.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(invoice => {
        if (statusFilter === 'pending') {
          // 'pending' filter matches 'pending_approval' status
          return invoice.status === 'pending_approval';
        } else if (statusFilter === 'auto-approved') {
          // Handle both 'auto-approved' (filter) and 'auto_approved' (actual status)
          return invoice.status === 'auto_approved' || invoice.approvalStatus === 'auto_approved';
        }
        return invoice.status === statusFilter;
      });
    }

    // Date range filter
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter(invoice => {
        const dueDate = new Date(invoice.dueDate);
        const start = dateRange.start ? new Date(dateRange.start) : null;
        const end = dateRange.end ? new Date(dateRange.end) : null;
        
        if (start && end) {
          return dueDate >= start && dueDate <= end;
        } else if (start) {
          return dueDate >= start;
        } else if (end) {
          return dueDate <= end;
        }
        return true;
      });
    }

    // Amount range filter
    if (amountRange.min || amountRange.max) {
      filtered = filtered.filter(invoice => {
        const amount = invoice.amount;
        const min = amountRange.min ? parseFloat(amountRange.min) : 0;
        const max = amountRange.max ? parseFloat(amountRange.max) : Infinity;
        
        return amount >= min && amount <= max;
      });
    }

    setFilteredInvoices(filtered);
  }, [invoices, searchQuery, statusFilter, dateRange, amountRange]);

  // Pagination logic
  const totalPages = Math.ceil(filteredInvoices.length / invoicesPerPage);
  const startIndex = (currentPage - 1) * invoicesPerPage;
  const endIndex = startIndex + invoicesPerPage;
  const currentInvoices = filteredInvoices.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, dateRange, amountRange]);

  // Close dropdowns when page changes
  useEffect(() => {
    setOpenDropdowns({});
  }, [currentPage]);

  // Sorting functionality
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredInvoices].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      if (key === 'supplier') {
        aValue = a.supplier.name;
        bValue = b.supplier.name;
      } else if (key === 'amount') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      } else if (key === 'dueDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredInvoices(sorted);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };

  const normalizeKey = (value) => (value || '').toLowerCase().replace(/-/g, '_');

  const getStatusBadge = (status, refundStatus, approvalStatus) => {
    // Check for refund status first
    if (refundStatus === 'full') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <RefreshCw className="w-3 h-3 mr-1" />
          FULLY REFUNDED
        </span>
      );
    }

    if (refundStatus === 'partial') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <RefreshCw className="w-3 h-3 mr-1" />
          PARTIALLY REFUNDED
        </span>
      );
    }

    const normalizedStatus = normalizeKey(status);
    const normalizedApproval = normalizeKey(approvalStatus);

    let displayKey = normalizedStatus || 'pending';

    if (['pending', 'pending_approval', 'sent', 'draft', ''].includes(displayKey) && normalizedApproval) {
      if (['approved', 'rejected', 'auto_approved'].includes(normalizedApproval)) {
        displayKey = normalizedApproval;
      } else if (['pending', 'pending_approval'].includes(normalizedApproval)) {
        displayKey = 'pending_approval';
      }
    }

    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      pending_approval: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle },
      auto_approved: { color: 'bg-blue-100 text-blue-800', icon: AlertTriangle },
      'auto-approved': { color: 'bg-blue-100 text-blue-800', icon: AlertTriangle },
      overdue: { color: 'bg-red-100 text-red-800', icon: XCircle },
      paid: { color: 'bg-green-100 text-green-800', icon: CheckCircle }
    };

    const config = statusConfig[displayKey] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {displayKey === 'pending_approval'
          ? 'PENDING APPROVAL'
          : displayKey.replace(/_/g, ' ').toUpperCase()}
      </span>
    );
  };

  const handleAction = async (type, invoice, event) => {
    if (type === 'view') {
      // Fetch full invoice details with line items
      try {
        setLoading(true);
        const response = await api.get(`/invoices/${invoice.id}`);
        const fullInvoice = response.data.data || response.data;
        
        // Merge full data with existing invoice data
        const completeInvoice = {
          ...invoice,
          ...fullInvoice,
          lineItems: fullInvoice.items || fullInvoice.lineItems || invoice.lineItems || []
        };
        
        setPreviewInvoice(completeInvoice);
        setShowPreview(true);
      } catch (error) {
        console.error('Error fetching full invoice details:', error);
        // Fallback to basic invoice data
        setPreviewInvoice(invoice);
        setShowPreview(true);
      } finally {
        setLoading(false);
      }
      return;
    }
    
    if (event) {
      // Capture position for dropdown positioning
      setDropdownPosition({ x: event.clientX, y: event.clientY });
    }
    setActionModal({ open: true, type, invoice });
    setSelectedInvoice(invoice);
  };

  const handleApprove = async () => {
    const invoiceId = selectedInvoice.id;
    
    // Immediately disable button by adding to approving set
    setApprovingInvoices(prev => new Set(prev).add(invoiceId));
    setApprovalErrors(prev => {
      const newMap = new Map(prev);
      newMap.delete(invoiceId); // Clear any previous error
      return newMap;
    });
    
    try {
      setLoading(true);
      
      // Send approval with payment authorization
      const response = await api.post(`/api/invoices/${invoiceId}/approve`, {
        paymentAuthorization: true
      });
      
      if (response.data.success) {
        const invoiceData = response.data.data?.invoice;
        const finalStatus = invoiceData?.status || 'approved';
        const wasProcessedImmediately = invoiceData?.processedImmediately || false;
        
        // Update local state - invoice might be 'paid' if it was overdue
        setInvoices(prev => prev.map(inv => 
          inv.id === invoiceId ? { 
            ...inv, 
            status: finalStatus,
            approvalStatus: 'approved'
          } : inv
        ));
        
        // Remove from approving set since it succeeded
        setApprovingInvoices(prev => {
          const newSet = new Set(prev);
          newSet.delete(invoiceId);
          return newSet;
        });
        
        setActionModal({ open: false, type: '', invoice: null });
        setSelectedInvoice(null);
        
        // Show appropriate success message based on what happened
        if (finalStatus === 'paid') {
          if (wasProcessedImmediately) {
            alert('✅ Invoice approved and payment processed successfully! The invoice was overdue, so payment was collected immediately.');
          } else {
            alert('✅ Invoice approved and payment processed successfully!');
          }
        } else if (response.data.warning) {
          alert(`⚠️ ${response.data.warning}`);
        } else {
          alert('✅ Invoice approved successfully! Payment will be collected on the due date.');
        }
        
        // Dispatch event to update integrations tab if auto-sync happened
        // This triggers refresh when invoices are auto-synced during approval
        window.dispatchEvent(new CustomEvent('invoiceSynced', {
          detail: {
            invoiceId: invoiceId,
            invoiceNumber: response.data?.data?.invoice?.invoiceNumber
          }
        }));
        
        // Refresh invoices to get updated data
        await fetchReceivedInvoices();
      } else {
        // Approval failed - check Stripe to confirm before re-enabling
        await checkApprovalFailureAndReenable(invoiceId);
        alert('Failed to approve invoice: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error approving invoice:', error);
      
      // Check if this is a timeout or network error (might have succeeded)
      const isTimeout = error.code === 'ECONNABORTED' || 
                       error.message?.includes('timeout') ||
                       error.response?.status === 504;
      
      if (isTimeout) {
        // Timeout - check if approval actually succeeded in Stripe before re-enabling
        console.log('⚠️ Approval request timed out - checking if approval succeeded in Stripe...');
        await checkApprovalFailureAndReenable(invoiceId);
        alert('Approval request timed out. Please check the invoice status - the approval may have succeeded. If the invoice still shows as pending, you can try again.');
      } else {
        // Confirmed error - check Stripe to confirm failure before re-enabling
        await checkApprovalFailureAndReenable(invoiceId);
        
        if (error.response?.status === 400) {
          if (error.response.data.message?.includes('linked bank account')) {
            alert('You must have a linked bank account to approve invoices. Please link your bank account in Settings first.');
          } else if (error.response.data.message?.includes('No payment method')) {
            alert('No payment method found. Please update your payment details in Settings before approving overdue invoices.');
          } else {
            alert('Invoice cannot be approved: ' + error.response.data.message);
          }
        } else if (error.response?.status === 403) {
          alert('You are not authorized to approve this invoice.');
        } else if (error.response?.status === 404) {
          alert('Invoice not found.');
        } else if (error.response?.data?.details) {
          alert('Payment processing error: ' + error.response.data.details);
        } else {
          alert('Error approving invoice. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Check if approval actually failed in Stripe before re-enabling the button
  const checkApprovalFailureAndReenable = async (invoiceId) => {
    try {
      // Fetch latest invoice status from backend
      const invoiceResponse = await api.get(`/invoices/${invoiceId}`);
      const latestInvoice = invoiceResponse.data.data || invoiceResponse.data;
      
      // Check if invoice was actually approved/paid (might have succeeded despite error)
      const isActuallyApproved = latestInvoice.status === 'approved' || 
                                 latestInvoice.status === 'paid' ||
                                 latestInvoice.approvalStatus === 'approved' ||
                                 latestInvoice.approvalStatus === 'auto_approved';
      
      if (isActuallyApproved) {
        // Approval actually succeeded - don't re-enable button, update UI
        console.log('✅ Approval actually succeeded despite error - invoice is approved');
        setApprovingInvoices(prev => {
          const newSet = new Set(prev);
          newSet.delete(invoiceId);
          return newSet;
        });
        
        // Update local state
        setInvoices(prev => prev.map(inv => 
          inv.id === invoiceId ? { 
            ...inv, 
            status: latestInvoice.status,
            approvalStatus: latestInvoice.approvalStatus || 'approved'
          } : inv
        ));
        
        // Refresh to get latest data
        await fetchReceivedInvoices();
        return; // Don't re-enable button
      }
      
      // Confirmed failure - re-enable button
      console.log('❌ Approval confirmed failed - re-enabling approve button');
      setApprovingInvoices(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoiceId);
        return newSet;
      });
      setApprovalErrors(prev => {
        const newMap = new Map(prev);
        newMap.set(invoiceId, true);
        return newMap;
      });
    } catch (checkError) {
      console.error('Error checking approval status:', checkError);
      // If we can't check, re-enable button to allow retry
      setApprovingInvoices(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoiceId);
        return newSet;
      });
    }
  };

  const handlePayNow = async () => {
    try {
      setLoading(true);
      
      // Send pay now request
      const response = await api.post(`/api/invoices/${selectedInvoice.id}/pay-now`);
      
      if (response.data.success) {
        const invoiceData = response.data.data?.invoice;
        const finalStatus = invoiceData?.status || 'paid';
        
        // Update local state - invoice should be 'paid' after Pay Now
        setInvoices(prev => prev.map(inv => 
          inv.id === selectedInvoice.id ? { 
            ...inv, 
            status: finalStatus,
            approvalStatus: 'approved'
          } : inv
        ));
        
        setActionModal({ open: false, type: '', invoice: null });
        setSelectedInvoice(null);
        
        // Show success message
        alert(`✅ Payment processed successfully! Invoice ${invoiceData?.invoiceNumber || selectedInvoice.invoiceNumber} has been paid.`);
        
        // Refresh invoices to get updated data
        await fetchReceivedInvoices();
      } else {
        alert('Failed to process immediate payment: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error processing immediate payment:', error);
      
      // CRITICAL: Check if invoice was actually paid (race condition handling)
      // If backend says "already paid", refresh invoice status to confirm
      if (error.response?.data?.code === 'ALREADY_PAID' || 
          error.response?.data?.error === 'Invoice already paid' ||
          error.response?.data?.message?.includes('already been paid')) {
        // Refresh invoice list to get latest status
        await fetchReceivedInvoices();
        
        // Show user-friendly message
        const message = error.response?.data?.message || 
                       'This invoice has already been paid. The payment is being processed and will update shortly. Please refresh the page in a few moments.';
        alert(`ℹ️ ${message}`);
        return; // Exit early - don't show error
      }
      
      // Handle specific error cases with user-friendly messages
      if (error.response?.data?.error) {
        // Backend already provided user-friendly error message
        alert(`❌ ${error.response.data.error}`);
      } else if (error.response?.status === 400) {
        if (error.response.data.message?.includes('linked bank account')) {
          alert('You must have a linked bank account to pay invoices. Please link your bank account in Settings first.');
        } else if (error.response.data.message?.includes('No payment method')) {
          alert('No payment method found. Please update your payment details in Settings.');
        } else {
          alert('Invoice cannot be paid: ' + error.response.data.message);
        }
      } else if (error.response?.status === 403) {
        alert('You are not authorized to pay this invoice.');
      } else if (error.response?.status === 404) {
        alert('Invoice not found or already processed.');
      } else {
        // For any other error (network, timeout, etc.), refresh invoice status
        // Payment might have succeeded in background
        console.log('⚠️ Payment error occurred - refreshing invoice status to check if payment succeeded');
        await fetchReceivedInvoices();
        alert('Payment processing encountered an issue. Please check the invoice status - the payment may have been processed successfully. If the invoice still shows as pending, please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection.');
      return;
    }

    try {
      setLoading(true);
      console.log('🚫 Rejecting invoice with reason:', rejectReason);
      const response = await api.post(`/api/invoices/${selectedInvoice.id}/reject`, {
        rejectionReason: rejectReason // Fixed: Lambda expects 'rejectionReason' not 'reason'
      });
      
      if (response.data.success) {
        // Update local state
        setInvoices(prev => prev.map(inv => 
          inv.id === selectedInvoice.id ? { 
            ...inv, 
            status: 'rejected',
            approvalStatus: 'rejected',
            rejectionReason: rejectReason
          } : inv
        ));
        
        setActionModal({ open: false, type: '', invoice: null });
        setSelectedInvoice(null);
        setRejectReason('');
        
        alert('Invoice rejected successfully. The supplier has been notified.');
      } else {
        alert('Failed to reject invoice: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error rejecting invoice:', error);
      if (error.response?.status === 400) {
        if (error.response.data.code === 'MISSING_REJECTION_REASON') {
          alert('Please provide a reason for rejection.');
        } else {
          alert('Invoice cannot be rejected in current status: ' + error.response.data.message);
        }
      } else if (error.response?.status === 403) {
        alert('You are not authorized to reject this invoice.');
      } else if (error.response?.status === 404) {
        alert('Invoice not found.');
      } else {
        alert('Error rejecting invoice. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnapprove = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for unapproval.');
      return;
    }

    if (selectedInvoice && isInvoicePaid(selectedInvoice)) {
      alert('This invoice has already been paid and cannot be unapproved.');
      return;
    }

    try {
      setLoading(true);
      console.log('🚫 Unapproving invoice with reason:', rejectReason);
      
      const response = await api.post(`/api/invoices/${selectedInvoice.id}/unapprove`, {
        reason: rejectReason
      });
      
      if (response.data.success) {
        // Check if invoice has early payment discount
        const hasEarlyPaymentDiscount = selectedInvoice.discountAmount && parseFloat(selectedInvoice.discountAmount) > 0;
        const discountAmount = selectedInvoice.discountAmount;
        
        // Build contextual success message
        let successMessage = '✅ Invoice unapproved successfully.\n\n';
        successMessage += '• Payment has been cancelled\n';
        
        if (hasEarlyPaymentDiscount) {
          successMessage += `• Early payment discount cancelled (£${discountAmount})\n`;
        }
        
        successMessage += '• Supplier has been notified\n';
        successMessage += '• Supplier must submit a new invoice';
        
        // Update local state - unapprove changes status to REJECTED
        setInvoices(prev => prev.map(inv => 
          inv.id === selectedInvoice.id ? { 
            ...inv, 
            status: 'rejected',
            approvalStatus: 'rejected',
            rejectionReason: rejectReason
          } : inv
        ));
        
        setActionModal({ open: false, type: '', invoice: null });
        setSelectedInvoice(null);
        setRejectReason('');
        
        alert(successMessage);
      } else {
        alert('Failed to unapprove invoice: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error unapproving invoice:', error);
      if (error.response?.status === 400) {
        if (error.response.data.message?.includes('already been paid')) {
          alert('❌ Cannot unapprove paid invoice.\n\nThis invoice has already been paid. Please use the refund process instead.');
        } else if (error.response.data.message?.includes('not approved')) {
          alert('This invoice is not approved and cannot be unapproved.');
        } else {
          alert('Error unapproving invoice: ' + error.response.data.message);
        }
      } else if (error.response?.status === 403) {
        alert('You are not authorized to unapprove this invoice.');
      } else if (error.response?.status === 404) {
        alert('Invoice not found.');
      } else {
        alert('Error unapproving invoice. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };


  const handleDownload = async (format, invoice, extraOptions = {}) => {
    try {
      await downloadInvoice(
        invoice,
        format === 'excel' ? 'csv' : format,
        { setLoading, ...extraOptions }
      );
    } catch (error) {
      console.warn('Invoice API download failed, using legacy renderer', error);
      try {
        await legacyHandleDownload(format, invoice);
      } catch (legacyError) {
        console.error('Error downloading invoice:', legacyError);
        alert('Failed to download invoice. Please try again.');
      }
    }
  };

  const handleDownloadAttachment = async (attachment, invoice) => {
    try {
      setLoading(true);
      
      // Get download URL from files Lambda
      const response = await api.get(`/api/files/download/${encodeURIComponent(attachment.s3Key)}?filename=${encodeURIComponent(attachment.name)}`);
      
      if (response.data.success && response.data.downloadUrl) {
        // Create a temporary link to trigger download with original filename
        const link = document.createElement('a');
        link.href = response.data.downloadUrl;
        link.download = attachment.name; // Use original filename
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Failed to download attachment. Please try again.');
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
      alert('Error downloading attachment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setDateRange({ start: '', end: '' });
    setAmountRange({ min: '', max: '' });
  };

  const formatCurrency = (amount) => {
    // Convert pence to pounds (amount is stored in pence in database)
    const amountInPounds = amount;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amountInPounds);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Received Invoices</h1>
          <p className="text-gray-600">Review and manage invoices received from suppliers</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchReceivedInvoices}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <svg className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
            {selectedInvoices.length > 0 && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleExportSelected}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV ({selectedInvoices.length})
                </button>
                <button
                  onClick={handleExportPDF}
                  className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Export PDF ({selectedInvoices.length})
                </button>
              </div>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
            >
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by Invoice, Supplier, or Email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="auto-approved">Auto-Approved</option>
              <option value="paid">Paid</option>
            </select>
          </div>

          {/* Clear Filters */}
          <div>
            <button
              onClick={clearFilters}
              className="w-full px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount Range</label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={amountRange.min}
                  onChange={(e) => setAmountRange(prev => ({ ...prev, min: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={amountRange.max}
                  onChange={(e) => setAmountRange(prev => ({ ...prev, max: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex items-end">
              <div className="text-sm text-gray-600">
                <p>Showing {filteredInvoices.length} of {invoices.length} invoices</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-lg border shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[450px]">
                          <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedInvoices(currentInvoices.map(inv => inv.id));
                        } else {
                          setSelectedInvoices([]);
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th 
                    className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-32"
                    onClick={() => handleSort('invoiceNumber')}
                  >
                  <div className="flex items-center space-x-1">
                    <span>Invoice</span>
                    {getSortIcon('invoiceNumber')}
                  </div>
                </th>
                <th 
                  className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-40"
                  onClick={() => handleSort('supplier')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Supplier</span>
                    {getSortIcon('supplier')}
                  </div>
                </th>
                <th 
                  className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-24"
                  onClick={() => handleSort('amount')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Amount</span>
                    {getSortIcon('amount')}
                  </div>
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                  Status
                </th>
                <th 
                  className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-28"
                  onClick={() => handleSort('dueDate')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Due Date</span>
                    {getSortIcon('dueDate')}
                  </div>
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentInvoices.map((invoice) => {
                const statusKey = normalizeKey(invoice.status);
                const approvalKey = normalizeKey(invoice.approvalStatus);
                return (
                  <tr key={invoice.id} className={`hover:bg-gray-50 ${
                    highlightedInvoiceId === invoice.id 
                      ? 'bg-blue-50 border-l-4 border-blue-500' 
                      : ''
                  }`}>
                    <td className="px-2 py-2 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedInvoices.includes(invoice.id)}
                        onChange={() => toggleInvoiceSelection(invoice.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{invoice.invoiceNumber}</div>
                      {invoice.description && invoice.description.trim() && (
                        <div className="text-sm text-gray-500 max-w-28 truncate">{invoice.description}</div>
                      )}
                      {invoice.poNumber && (
                        <div className="text-xs text-blue-600 font-medium">
                          PO: {invoice.poNumber}
                        </div>
                      )}
                    </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 max-w-32 truncate">{invoice.supplier.name}</div>
                    <div className="text-sm text-gray-500 max-w-32 truncate">{invoice.supplier.email}</div>
                  </td>
                  <td className="px-2 py-2">
                    <div className="text-sm font-medium text-gray-900 mb-2">{formatCurrency(invoice.amount)}</div>
                    {/* Early Payment Banner (Compact) */}
                    <div className="max-w-xs">
                      <EarlyPaymentBanner 
                        invoice={invoice} 
                        viewType="receiver" 
                        compact={true}
                      />
                    </div>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {getStatusBadge(invoice.status, invoice.refundStatus, invoice.approvalStatus)}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {/* Only show due date for non-rejected/non-disputed invoices */}
                    {invoice.status !== 'rejected' && invoice.status !== 'disputed' ? (
                      <>
                        <div className="text-sm text-gray-900">{formatDate(invoice.dueDate)}</div>
                        <div className="text-sm text-gray-500">Received: {formatDate(invoice.receivedDate)}</div>
                      </>
                    ) : (
                      <div className="text-sm text-gray-400">—</div>
                    )}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    <div className="relative actions-dropdown">
                      {/* Actions Dropdown Button */}
                      <button 
                        onClick={(e) => {
                          // Capture button position for dropdown
                          const rect = e.currentTarget.getBoundingClientRect();
                          setDropdownPosition({
                            x: rect.left,
                            y: rect.bottom
                          });
                          
                          // Close all other dropdowns and toggle current one
                          setOpenDropdowns(prev => {
                            const newState = {};
                            Object.keys(prev).forEach(key => {
                              newState[key] = false; // Close all dropdowns first
                            });
                            // Toggle the current one
                            newState[invoice.id] = !prev[invoice.id];
                            // Update dropdown state
                            return newState;
                          });
                        }}
                        className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                      

                      
                      {/* Actions Dropdown */}
                      {openDropdowns[invoice.id] === true && (
                        <div className="fixed w-32 bg-blue-100 border-2 border-blue-500 rounded-lg shadow-lg z-[9999]" style={{
                          left: `${Math.max(10, Math.min(window.innerWidth - 140, dropdownPosition.x))}px`,
                          top: `${Math.min(window.innerHeight - 150, Math.max(10, dropdownPosition.y + 5))}px`
                        }}>
                          <div className="py-1">
                            {/* View Details - Always Available */}
                            <button
                              onClick={(e) => handleAction('view', invoice, e)}
                              className="w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                            >
                              <Eye className="w-3 h-3 text-blue-600" />
                              <span>View Details</span>
                            </button>

                            {/* Approve Button - only show for pending_approval invoices */}
                            {(statusKey === 'pending_approval' || approvalKey === 'pending' || approvalKey === 'pending_approval') && (
                              <button
                                onClick={(e) => handleAction('approve', invoice, e)}
                                disabled={approvingInvoices.has(invoice.id)}
                                className={`w-full px-3 py-1.5 text-left text-xs flex items-center space-x-2 ${
                                  approvingInvoices.has(invoice.id)
                                    ? 'text-gray-400 cursor-not-allowed opacity-50'
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                                title={approvingInvoices.has(invoice.id) ? 'Approval in progress...' : 'Approve Invoice'}
                              >
                                <CheckCircle className={`w-3 h-3 ${approvingInvoices.has(invoice.id) ? 'text-gray-400' : 'text-green-600'}`} />
                                <span>{approvingInvoices.has(invoice.id) ? 'Approving...' : 'Approve Invoice'}</span>
                              </button>
                            )}

                            {/* Reject Button - only show for pending_approval invoices */}
                            {(statusKey === 'pending_approval' || approvalKey === 'pending' || approvalKey === 'pending_approval') && (
                              <button
                                onClick={(e) => handleAction('reject', invoice, e)}
                                className="w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                              >
                                <XCircle className="w-3 h-3 text-red-600" />
                                <span>Reject Invoice</span>
                              </button>
                            )}

                            {/* Unapprove Button - only show for approved & unpaid invoices */}
                            {approvalKey === 'approved' && !isInvoicePaid(invoice) && (
             <button
               onClick={(e) => handleAction('unapprove', invoice, e)}
               className="w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
             >
               <XCircle className="w-3 h-3 text-orange-600" />
               <span>Unapprove</span>
             </button>
           )}

                            {/* Attachments - show if invoice has attachments */}
                            {invoice.attachments && invoice.attachments.length > 0 && (
                              <button
                                onClick={(e) => handleAction('attachments', invoice, e)}
                                className="w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                              >
                                <Paperclip className="w-3 h-3 text-purple-600" />
                                <span>Attachments ({invoice.attachments.length})</span>
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {filteredInvoices.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <FileText className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices found</h3>
            <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div className="flex-1 text-sm text-center text-gray-700">
              Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Action Modals */}
      {actionModal.open && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            {/* Pay Now Modal */}
            {actionModal.type === 'pay-now' && (
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <PoundSterling className="w-8 h-8 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Pay Now - Immediate Payment</h3>
                </div>
                
                {/* Invoice Details */}
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Invoice:</span>
                      <span className="font-medium ml-2">{selectedInvoice?.invoiceNumber}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Amount:</span>
                      <span className="font-medium ml-2">{formatCurrency(selectedInvoice?.amount)}</span>
                    </div>
                    {selectedInvoice?.poNumber && (
                      <div>
                        <span className="text-gray-600">PO #:</span>
                        <span className="font-medium ml-2">{selectedInvoice.poNumber}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-600">Due Date:</span>
                      <span className="font-medium ml-2">{formatDate(selectedInvoice?.dueDate)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Supplier:</span>
                      <span className="font-medium ml-2">{selectedInvoice?.supplier?.name}</span>
                    </div>
                  </div>
                </div>

                {/* Immediate Payment Section */}
                <div className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">Immediate Payment</h4>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 text-sm font-medium">⚡</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-blue-800">
                          By clicking "Pay Now", you authorize immediate payment of <strong>{formatCurrency(selectedInvoice?.amount)}</strong> to be processed right now, bypassing the due date.
                        </p>
                        <p className="text-xs text-blue-600 mt-2">
                          Payment will be processed immediately using Stripe Connect.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={handlePayNow}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    Pay Now
                  </button>
                  <button
                    onClick={() => setActionModal({ open: false, type: '', invoice: null })}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Approve Modal */}
            {actionModal.type === 'approve' && (() => {
              // Calculate if invoice is overdue
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const dueDate = new Date(selectedInvoice?.dueDate);
              dueDate.setHours(0, 0, 0, 0);
              const isOverdue = dueDate.getTime() < today.getTime();
              const isDueToday = dueDate.getTime() === today.getTime();
              
              // Check Stripe minimum amount
              const minCheck = checkStripeMinimum(selectedInvoice?.amount, selectedInvoice?.currency);
              const isBelowMinimum = minCheck.isBelowMinimum;
              
              return (
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Approve Invoice & Choose Payment Option</h3>
                  </div>
                  
                  {/* Overdue Warning Banner */}
                  {isOverdue && (
                    <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4 mb-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-orange-900 mb-1">⚠️ Overdue Invoice</p>
                          <p className="text-xs text-orange-700">
                            This invoice was due on <strong>{formatDate(selectedInvoice?.dueDate)}</strong> and is now <strong>overdue</strong>. Payment will be collected <strong>immediately</strong> upon approval.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Invoice Details */}
                  <div className="bg-gray-50 p-4 rounded-lg mb-6">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Invoice:</span>
                        <span className="font-medium ml-2">{selectedInvoice?.invoiceNumber}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-medium ml-2">{formatCurrency(selectedInvoice?.amount)}</span>
                      </div>
                      {selectedInvoice?.poNumber && (
                        <div>
                          <span className="text-gray-600">PO #:</span>
                          <span className="font-medium ml-2">{selectedInvoice.poNumber}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-600">Due Date:</span>
                        <span className={`font-medium ml-2 ${isOverdue ? 'text-orange-600 font-bold' : ''}`}>
                          {formatDate(selectedInvoice?.dueDate)}
                          {isOverdue && <span className="ml-1 text-xs">(OVERDUE)</span>}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Supplier:</span>
                        <span className="font-medium ml-2">{selectedInvoice?.supplier?.name}</span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Options Section */}
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3">Choose Payment Option</h4>
                    <p className="text-xs text-gray-600 mb-4">
                      Select how you'd like to pay this invoice of <strong>{formatCurrency(selectedInvoice?.amount)}</strong>
                    </p>
                    
                    {/* Minimum Amount Warning */}
                    {isBelowMinimum && (
                      <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-4">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-red-900 mb-1">⚠️ Amount Below Minimum</p>
                            <p className="text-xs text-red-700">
                              {getMinimumAmountError(selectedInvoice?.amount, selectedInvoice?.currency)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    {/* Option 1: Pay Now with Saved Card */}
                    <button
                      onClick={handlePayNow}
                      disabled={loading || isBelowMinimum}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={isBelowMinimum ? getMinimumAmountError(selectedInvoice?.amount, selectedInvoice?.currency) : ''}
                    >
                      <PoundSterling className="w-5 h-5" />
                      <span>{loading ? 'Processing...' : 'Pay Now (Saved Card)'}</span>
                    </button>
                    
                    {/* Option 2: Pay with Different Card (NEW) */}
                    <button
                      onClick={async () => {
                        try {
                          setLoading(true);
                          const response = await api.post('/api/invoices/create-checkout', {
                            invoiceId: selectedInvoice.id
                          });
                          
                          if (response.data.success && response.data.url) {
                            // Redirect to Stripe Checkout
                            window.location.href = response.data.url;
                          } else {
                            alert('Failed to create checkout session. Please try again.');
                          }
                        } catch (error) {
                          console.error('Error creating checkout session:', error);
                          alert(error.response?.data?.error || 'Failed to create checkout session. Please try again.');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading || isBelowMinimum}
                      className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200 font-medium flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={isBelowMinimum ? getMinimumAmountError(selectedInvoice?.amount, selectedInvoice?.currency) : ''}
                    >
                      <CreditCard className="w-5 h-5" />
                      <span>{loading ? 'Loading...' : 'Pay with Different Card'}</span>
                    </button>
                    
                    {/* Option 3: Approve for Due Date (only if not overdue) */}
                    {!isOverdue && (
                      <button
                        onClick={handleApprove}
                        disabled={loading || isBelowMinimum || (selectedInvoice && approvingInvoices.has(selectedInvoice.id))}
                        className={`w-full px-4 py-3 rounded-lg transition-colors duration-200 font-medium flex items-center justify-center space-x-2 ${
                          (loading || (selectedInvoice && approvingInvoices.has(selectedInvoice.id)))
                            ? 'bg-gray-400 text-white cursor-not-allowed opacity-50'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        title={isBelowMinimum ? getMinimumAmountError(selectedInvoice?.amount, selectedInvoice?.currency) : 
                               (selectedInvoice && approvingInvoices.has(selectedInvoice.id)) ? 'Approval in progress...' : ''}
                      >
                        <CheckCircle className="w-5 h-5" />
                        <span>
                          {(loading || (selectedInvoice && approvingInvoices.has(selectedInvoice.id))) 
                            ? 'Processing...' 
                            : isDueToday 
                              ? 'Approve & Pay Today' 
                              : 'Approve for Due Date'}
                        </span>
                      </button>
                    )}
                    
                    {/* Cancel Button */}
                    <button
                      onClick={() => setActionModal({ open: false, type: '', invoice: null })}
                      disabled={loading}
                      className="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Reject Modal */}
            {actionModal.type === 'reject' && (
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <XCircle className="w-8 h-8 text-red-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Reject Invoice</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Please provide a detailed reason for rejecting this invoice. The supplier will be notified immediately.
                </p>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="e.g., Incorrect pricing, wrong quantities, duplicate invoice, etc. (minimum 10 characters)"
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
                />
                <p className="text-xs text-gray-500 mb-4">
                  {rejectReason.trim().length < 10 
                    ? `Please provide at least 10 characters (${rejectReason.trim().length}/10)`
                    : `${rejectReason.trim().length} characters`
                  }
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={handleReject}
                    disabled={!rejectReason.trim() || rejectReason.trim().length < 10 || loading}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Rejecting...' : 'Reject Invoice'}
                  </button>
                  <button
                    onClick={() => setActionModal({ open: false, type: '', invoice: null })}
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Unapprove Modal */}
            {actionModal.type === 'unapprove' && (
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <XCircle className="w-8 h-8 text-orange-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Unapprove Invoice</h3>
                </div>
                
                {/* Invoice Details */}
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Invoice:</span>
                      <span className="font-medium ml-2">{selectedInvoice?.invoiceNumber}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Amount:</span>
                      <span className="font-medium ml-2">{formatCurrency(selectedInvoice?.amount)}</span>
                    </div>
                    {selectedInvoice?.poNumber && (
                      <div>
                        <span className="text-gray-600">PO #:</span>
                        <span className="font-medium ml-2">{selectedInvoice.poNumber}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-600">Due Date:</span>
                      <span className="font-medium ml-2">{formatDate(selectedInvoice?.dueDate)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Supplier:</span>
                      <span className="font-medium ml-2">{selectedInvoice?.supplier?.name}</span>
                    </div>
                  </div>
                </div>

                <p className="text-gray-600 mb-4">
                  Please provide a reason for unapproving this invoice. The scheduled payment will be cancelled and the supplier will be notified immediately.
                </p>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter unapproval reason..."
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
                />
                <div className="flex space-x-3">
                  <button
                    onClick={handleUnapprove}
                    disabled={!rejectReason.trim() || loading}
                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Unapproving...' : 'Unapprove Invoice'}
                  </button>
                  <button
                    onClick={() => setActionModal({ open: false, type: '', invoice: null })}
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* View Details Modal */}
            {actionModal.type === 'view' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Invoice Details</h3>
                  <button
                    onClick={() => setActionModal({ open: false, type: '', invoice: null })}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Invoice Information</h4>
                    <div className="bg-gray-50 p-3 rounded-lg space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Invoice:</span>
                        <span className="font-medium">{selectedInvoice?.invoiceNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-medium">{formatCurrency(selectedInvoice?.amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span>{getStatusBadge(selectedInvoice?.status, selectedInvoice?.refundStatus, selectedInvoice?.approvalStatus)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Due Date:</span>
                        <span className="font-medium">{formatDate(selectedInvoice?.dueDate)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Line Items</h4>
                    <div className="space-y-2">
                      {selectedInvoice?.lineItems.map((item, index) => (
                        <div key={index} className="flex justify-between text-sm bg-gray-50 p-2 rounded">
                          <span>{item.description}</span>
                          <span className="font-medium">{formatCurrency(item.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {selectedInvoice?.notes && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                      <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{selectedInvoice.notes}</p>
                    </div>
                  )}

                  {selectedInvoice?.rejectReason && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Rejection Reason</h4>
                      <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{selectedInvoice.rejectReason}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Attachments Modal */}
            {actionModal.type === 'attachments' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Invoice Attachments</h3>
                  <button
                    onClick={() => setActionModal({ open: false, type: '', invoice: null })}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div className="text-sm text-gray-600 mb-4">
                    <p>Invoice {selectedInvoice?.invoiceNumber} • {selectedInvoice?.attachments?.length || 0} attachment(s)</p>
                  </div>
                  
                  {selectedInvoice?.attachments && selectedInvoice.attachments.length > 0 ? (
                    <div className="space-y-3">
                      {selectedInvoice.attachments.map((attachment, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border">
                          <div className="flex items-center space-x-3">
                            <Paperclip className="w-5 h-5 text-blue-600" />
                            <div>
                              <p className="font-medium text-gray-900">{attachment.name || attachment}</p>
                              {typeof attachment === 'object' && attachment.size && (
                                <p className="text-sm text-gray-500">{attachment.size}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleDownloadAttachment(attachment, selectedInvoice)}
                              className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
                            >
                              <Download className="w-4 h-4" />
                              <span>Download</span>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Paperclip className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">No attachments found for this invoice.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invoice Preview Modal with Early Payment Banner */}
      {showPreview && previewInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              {/* Early Payment Banner (Full) */}
              <EarlyPaymentBanner 
                invoice={previewInvoice} 
                viewType="receiver" 
                compact={false}
              />
              
              {/* Invoice Preview */}
              <InvoicePreview
                invoice={previewInvoice}
                isOpen={true}
                onClose={() => {
                  setShowPreview(false);
                  setPreviewInvoice(null);
                }}
                onDownloadPDF={(invoice) => handleDownloadPDF(invoice, { forcePrintablePreview: true })}
                onDownloadExcel={(invoice) => handleDownloadExcel(invoice)}
                showDownloadButtons={true}
                title="Received Invoice Details"
                hideModal={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceivedInvoices;
